/**
 * useActiveBranding — resolves the firm branding that should paint the app
 * RIGHT NOW, from two sources in precedence order:
 *
 *   1. Host subdomain (`iprotect.elreign.io` / `?tenant=` dev override) — the
 *      explicit white-label context; works pre- AND post-login via the public
 *      by-slug endpoint.
 *   2. The logged-in user's tenant (`/v1/tenants/me`) — so a user on the bare
 *      apex `app.elreign.io` (no subdomain) still sees their firm's colours.
 *      While a firm administers a client (act-as), this resolves the FIRM's
 *      brand via the operator session, not the act-as client's tenant.
 *
 * Returns the display fields the runtime + nav chrome need, or `null` (→ the
 * baked Elreign default). The colour injection itself lives in
 * `branding-runtime.ts`; this hook only decides WHICH branding is active.
 *
 * Both queries are cache-first (60s staleTime + the AFD edge cache), and the
 * root already kicks the by-slug query, so calling this in the nav shells is a
 * free cache read — no extra network round-trip.
 */

import { useSession } from './use-session';
import { resolveTenantSlugFromHost } from './tenant';
import { useTenantBrandingBySlug, useTenantMe } from './api';
import type { PublicTenantBranding } from './api';
import { useIsActingAsClient } from './acting/context';

export interface ActiveBranding {
  primaryColor: string | null;
  accentColor: string | null;
  firmName: string | null;
  logoUrl: string | null;
  fontPair: string | null;
}

/** True when an overlay carries something paintable (colour, logo, or name). */
function hasVisualBranding(b: PublicTenantBranding | null | undefined): boolean {
  return !!(b && (b.primaryColor || b.accentColor || b.logoUrl || b.firmName));
}

function toActive(b: PublicTenantBranding): ActiveBranding {
  return {
    primaryColor: b.primaryColor ?? null,
    accentColor: b.accentColor ?? null,
    firmName: b.firmName ?? null,
    logoUrl: b.logoUrl ?? null,
    fontPair: b.fontPair ?? null,
  };
}

/**
 * Pure precedence: host (subdomain) branding wins; otherwise the authed
 * tenant's. Returns null when neither carries anything paintable. Exported for
 * unit testing — the hook is thin glue over this.
 */
export function pickActiveBranding(
  host: PublicTenantBranding | null | undefined,
  authed: PublicTenantBranding | null | undefined,
): ActiveBranding | null {
  if (hasVisualBranding(host)) return toActive(host!);
  if (hasVisualBranding(authed)) return toActive(authed!);
  return null;
}

/** Synchronous host-slug resolution (SSR-safe). */
export function resolveHostSlug(): string | null {
  if (typeof window === 'undefined') return null;
  return resolveTenantSlugFromHost(
    window.location.host,
    new URLSearchParams(window.location.search),
  );
}

export function useActiveBranding(): ActiveBranding | null {
  const slug = resolveHostSlug();
  const hostQuery = useTenantBrandingBySlug(slug);

  // Only hit /v1/tenants/me once authenticated — pre-login it would 401, and on
  // a tenant subdomain the by-slug query already covers the pre-login case.
  const { session } = useSession();

  // First-class tenancy: when a firm-user is administering a client (act-as),
  // data calls travel on the act-as token, so a plain /tenants/me is scoped to
  // the CLIENT's tenant. But the app should keep painting in the FIRM's brand —
  // the white-label promise — so under act-as we resolve branding from the firm
  // via the operator's own session instead of the act-as client. (On a firm
  // subdomain the host overlay wins regardless; this only bites on bare apex.)
  const actingAsClient = useIsActingAsClient();
  const clientMeQuery = useTenantMe({ enabled: !!session });
  const firmMeQuery = useTenantMe({
    enabled: !!session && actingAsClient,
    useOperatorSession: true,
  });
  const authedBranding = actingAsClient
    ? (firmMeQuery.data?.branding ?? null)
    : (clientMeQuery.data?.branding ?? null);

  return pickActiveBranding(hostQuery.data?.branding, authedBranding);
}
